import { InputAdornment, withWidth } from '@material-ui/core';
import InputBase from '@material-ui/core/InputBase';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import DateRange from '@material-ui/icons/DateRange';
import ExpandMore from '@material-ui/icons/ExpandMore';
import Paper from '@material-ui/core/Paper';
import subDays from 'date-fns/subDays';
import flowRight from 'lodash/flowRight';
import get from 'lodash/get';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';

import LineChart from './LineChart';
import PieChart from './PieChart';
import * as ChartConstants from './Home/ChartConstants';
import { homePageStyles, v2DropdownStyles } from '../../css/style';
import {
  months, pieChartLegend, lineChartOptions,
} from '../../data/Constants';
import * as ChartUtils from '../../utils/ChartUtils';
import * as ChartTooltips from '../../utils/ChartTooltips';
import * as Functions from '../../utils/Functions';
import LoadingBar from '../share/LoadingBar';
import VersionAndBuildNumber from '../share/VersionAndBuildNumber';
import LegendColorBox from './LegendColorBox';
import LegendText from './LegendText';
import * as PromiseAllSettled from '../../utils/PromiseAllSettled';
import * as Selectors from '../../selectors/index';

const styles = homePageStyles();
const dropdownStyles = v2DropdownStyles();

export class Home extends Component {
  constructor(props) {
    super(props);

    this.state = {
      timescale: ChartConstants.TIMESCALES.thirtyDays,
    };
    this.getOrdersChartTooltipTitle = this.getOrdersChartTooltipTitle.bind(this);
  }

  async componentDidMount() {
    await this.getHomeResources();
  }

  componentDidUpdate = async (prevProps) => {
    const { apiToken } = this.props;
    if (prevProps.apiToken !== apiToken) {
      await this.getHomeResources(true);
    }
  }

  getHomeResources = async (forceFetch = false) => {
    const { loading, history } = this.props;
    if (!loading) {
      const needsToFetch = this.checkNeedsToFetch() || forceFetch;

      if (needsToFetch) {
        await this.fetchInitialResources();
      }
    }
    if (history !== undefined) {
      history.push('/');
    }
  }

  checkNeedsToFetch = () => {
    const { resources } = this.props;
    let needsToFetch = true;
    Object.keys(resources).forEach((key) => {
      if (needsToFetch === false) return;
      if (ChartConstants.ORDERS_ANALYTICS_ROUTE_REGEX.test(key)) {
        needsToFetch = false;
      }
    });
    return needsToFetch;
  }

  fetchInitialResources = async () => {
    const now = new Date();
    const startDate = subDays(now, 30);
    const dateQueryParams = ChartUtils.getAnalyticsDatesQueryParams(startDate, now);
    const finalPaths = [
      `${ChartConstants.ORDERS_ANALYTICS_API_ROUTE}?${dateQueryParams}`,
      `${ChartConstants.DOWNLOADS_ANALYTICS_API_ROUTE}?${dateQueryParams}`,
      'locations',
    ];

    await this.callEndpointsInParallel(finalPaths, 'API call errors during Home.fetchInitialResources');
  }

  callEndpointsInParallel = async (paths, errorLabel) => {
    const { actions, apiToken } = this.props;
    const promises = paths.map(async (path) => {
      if (ChartUtils.isAHomeAnalyticsRoute(path)) return await actions.getHomeAnalytics(apiToken, path);
      return await actions.getAllResources(apiToken, path);
    });
    const results = await Promise.allSettled(promises);
    PromiseAllSettled.logErrorResults(results, errorLabel);
  }

  fetchAnalytics = async (startDate) => {
    const now = new Date();
    const dateQueryParams = ChartUtils.getAnalyticsDatesQueryParams(startDate, now);

    const finalPaths = [
      `${ChartConstants.ORDERS_ANALYTICS_API_ROUTE}?${dateQueryParams}`,
      `${ChartConstants.DOWNLOADS_ANALYTICS_API_ROUTE}?${dateQueryParams}`,
    ];

    await this.callEndpointsInParallel(finalPaths, 'API call error on change Analytics timescale');
  }

  getOrdersByDeliveryOptionChart = items => this.getPieChart(items, ChartUtils.setDeliveryOptionOrderInfoOnChartData);

  setOrdersByLocationInfoOnChartData = (ordersByLocation, chartData, datasetObject) => {
    const { resources: { locations } } = this.props;

    if (ordersByLocation.length > 0) {
      locations.map((loc, index) => {
        chartData.labels.push(loc.name);
        const ordersOfLoc = ordersByLocation.filter(item => parseInt(item[0]) === loc.id);
        const cnt = ordersOfLoc.length ? ordersOfLoc[0][1] : 0;
        datasetObject.data.push(cnt);
        datasetObject.backgroundColor.push(ChartConstants.PIE_DATA_AND_LINE_DATASET_COLORS[index]);
      });
    }
    chartData.datasets.push(datasetObject);
    return chartData;
  };

  getPieChart = (rawData, chartDataSetterFunc) => {
    const { loadingOrdersAnalytics } = this.props;

    const { timescale } = this.state;
    const chartConfigOptions = timescale === ChartConstants.TIMESCALES.allTime
      ? ChartConstants.NO_ANIMATION_PIE_CHART_OPTIONS
      : ChartConstants.BASE_PIE_CHART_OPTIONS;
    let chartData = ChartUtils.getBaseChartDataObject();
    const datasetObject = ChartUtils.getBasePieConfigDatasetObject();
    chartData = chartDataSetterFunc(rawData, chartData, datasetObject);

    return (
      <PieChart
        isLoading={loadingOrdersAnalytics}
        data={chartData}
        height={300}
        options={chartConfigOptions}
        width={150}
        legendPosition="below"
        legend={pieChartLegend}
        legendStat="count"
        legendStatSuffix="Orders"
      />
    );
  }

  getOrdersByLocationChart = items => this.getPieChart(items, this.setOrdersByLocationInfoOnChartData);

  ordersChartDynamicTooltipContent = {
    title: this.getOrdersChartTooltipTitle.bind(this),
    label: ChartTooltips.getDateLabelOfPoint,
  };

  getOrdersChartTooltipTitle(tooltipItem, data) {
    const { index } = tooltipItem[0];
    const timescaleData = this.getDataForRouteKey(ChartConstants.ORDERS_ANALYTICS_API_ROUTE);
    const { sales, orders } = timescaleData;
    const commonArrayPath = `[${index}][1]`;
    const dailyOrderCount = get(orders, commonArrayPath, '');
    const dailySales = get(sales, commonArrayPath, '');
    const formattedDailySales = `$${Functions.showTwoDecimalsForMoney(dailySales)}`;
    return `${dailyOrderCount} - ${formattedDailySales}`;
  }

  ordersChartOptions = {
    tooltips: {
      ...ChartConstants.BASE_TOOLTIP_OPTIONS,
      callbacks: this.ordersChartDynamicTooltipContent,
    },
    maintainAspectRatio: false,
    ...ChartConstants.BASE_AXES_CONFIG_OPTIONS,
  };

  ordersChartNoAnimation = {
    ...this.ordersChartOptions,
    ...ChartConstants.NO_ANIMATIONS_FOR_PERFORMANCE,
  }

  chartOptions = {
    orders: {
      normal: this.ordersChartOptions,
      noAnimation: this.ordersChartNoAnimation,
    },
    signups: {
      normal: ChartConstants.SIGNUPS_CHART_OPTIONS,
      noAnimation: ChartConstants.SIGNUPS_CHART_NO_ANIMATION_OPTIONS,
    },
  };

  getOrdersAnalyticsSingleLineChart = (collection, uniqueKey) => {
    const { loadingOrdersAnalytics } = this.props;

    let chartData = ChartUtils.getBaseChartDataObject();
    const datasetObject = ChartUtils.getBaseOneLineConfig();
    const { timescale } = this.state;
    const setLineDataPointPropertiesArgs = {
      rawData: collection,
      datasetObject,
      chartData,
      timescale,
    };
    chartData = ChartUtils.setLineDataPointPropertiesFromRaw(setLineDataPointPropertiesArgs);

    const normalOrNoAnim = timescale === ChartConstants.TIMESCALES.allTime ? 'noAnimation' : 'normal';

    const chartConfigOptions = this.chartOptions[uniqueKey][normalOrNoAnim];
    return (
      <LineChart
        isLoading={loadingOrdersAnalytics}
        data={chartData}
        legend={{ display: false }}
        options={chartConfigOptions}
      />
    );
  };

  getDataForRouteKey = (key) => {
    const data = get(this.props, `resources['${key}']`);
    return data;
  }

  getDownloadsChart = () => {
    const { timescale } = this.state;
    const { loadingDownloadsAnalytics } = this.props;

    let chartData = ChartUtils.getBaseChartDataObject();
    // const downloadsResponseData = this.getDataForRouteKey(ChartConstants.DOWNLOADS_ANALYTICS_API_ROUTE);

    // chartData = ChartUtils.setMultipleDatasetsOnOneLineChart(downloadsResponseData, chartData);

    const baseOptions = timescale === ChartConstants.TIMESCALES.allTime
      ? ChartConstants.DOWNLOADS_CHART_NO_ANIMATION_OPTIONS
      : ChartConstants.DOWNLOADS_CHART_OPTIONS;

    return (
      <LineChart
        isLoading={loadingDownloadsAnalytics}
        data={chartData}
        legend={{ display: false }}
        options={baseOptions}
        isComingSoon
      />
    );
  }

  getChartTitles = () => {
    const returnObj = {};

    Object.entries(ChartConstants.CHART_TITLES_CONFIG).forEach(([chartKey, chartConfig]) => {
      const { titleFirstSegment, totalsToCalculate, routeKey } = chartConfig;
      const timescaleData = this.getDataForRouteKey(routeKey);
      if (chartKey === 'downloads') {
        returnObj.downloadsTitle = 'Downloads - Coming Soon';
        return;
      }
      let title = titleFirstSegment;
      title = ChartUtils.addCalculatedTitleStat(totalsToCalculate, timescaleData, title);

      returnObj[`${chartKey}Title`] = title;
    });

    return returnObj;
  }

  changeTimescale = (e) => {
    const { value } = e.target;
    this.setState({ timescale: value });
    let startDate = new Date();
    if (value === ChartConstants.TIMESCALES.sevenDays) startDate = subDays(startDate, 7);
    if (value === ChartConstants.TIMESCALES.thirtyDays) startDate = subDays(startDate, 30);
    // The API will do an allTime fetch going back to the company's createdAt date if start-date or end-date is not provided
    if (value === ChartConstants.TIMESCALES.allTime) startDate = null;

    this.fetchAnalytics(startDate);
  };

  renderHomePage = () => {
    const { timescale } = this.state;
    const orderAnalyticsData = this.getDataForRouteKey(ChartConstants.ORDERS_ANALYTICS_API_ROUTE);
    const {
      orders,
      users,
      delivery_types,
      order_location,
    } = orderAnalyticsData;

    const { downloadsTitle, signupsTitle, ordersTitle } = this.getChartTitles();

    return (
      <React.Fragment>
        <div style={styles.timescaleAndChartsFlexColumnDiv}>
          {this.renderTopBar(timescale)}
          <div style={styles.container}>
            <div style={styles.leftVerticalChartsContainer}>
              <DownloadsChart
                renderChart={this.getDownloadsChart()}
                title={downloadsTitle}
              />
              <OneQuadrantLineChart
                renderChart={this.getOrdersAnalyticsSingleLineChart(orders || [], 'orders')}
                title={ordersTitle}
              />
              <VersionAndBuildNumber containerStyle={styles.versionAndBuildNumber} />
            </div>
            <div style={styles.rightVerticalChartsContainer}>
              <OneQuadrantLineChart
                renderChart={this.getOrdersAnalyticsSingleLineChart(users || [], 'signups')}
                title={signupsTitle}
              />
              <Paper style={styles.paper}>
                <div style={styles.twoChartsInOneQuadrantFlexRowDiv}>
                  <HalfQuadrantPieChart
                    renderChart={this.getOrdersByDeliveryOptionChart(delivery_types || [])}
                    title="By Dining Option"
                  />
                  <HalfQuadrantPieChart
                    renderChart={this.getOrdersByLocationChart(order_location || [])}
                    title="By Location"
                  />
                </div>
              </Paper>
              <div style={styles.versionAndBuildNumberLikeSpacer} />
            </div>
          </div>
        </div>
      </React.Fragment>
    );
  };

  renderMobileHomePage = () => {
    const { timescale } = this.state;
    const orderAnalyticsData = this.getDataForRouteKey(ChartConstants.ORDERS_ANALYTICS_API_ROUTE);
    const {
      orders,
      users,
      delivery_types,
      order_location,
    } = orderAnalyticsData;

    const { downloadsTitle, signupsTitle, ordersTitle } = this.getChartTitles();

    return (
      <React.Fragment>
        <div style={styles.timescaleAndChartsFlexColumnDiv}>
          {this.renderTopBar(timescale)}
          <div style={styles.mobileContainer}>
              <DownloadsChart
                renderChart={this.getDownloadsChart()}
                title={downloadsTitle}
                isMobile
              />
              <OneQuadrantLineChart
                renderChart={this.getOrdersAnalyticsSingleLineChart(orders || [], 'orders')}
                title={ordersTitle}
                isMobile
              />
              <OneQuadrantLineChart
                renderChart={this.getOrdersAnalyticsSingleLineChart(users || [], 'signups')}
                title={signupsTitle}
                isMobile
              />
              <Paper style={styles.mobilePaper}>
                <div style={styles.twoChartsInOneQuadrantFlexRowDiv}>
                  <HalfQuadrantPieChart
                    renderChart={this.getOrdersByDeliveryOptionChart(delivery_types || [])}
                    title="By Dining Option"
                  />
                  <HalfQuadrantPieChart
                    renderChart={this.getOrdersByLocationChart(order_location || [])}
                    title="By Location"
                  />
                </div>
              </Paper>
              <VersionAndBuildNumber containerStyle={styles.versionAndBuildNumber} />
          </div>
        </div>
      </React.Fragment>
    );
  }

  renderTopBar(timescale) {
    return (
      <div style={styles.timescaleDropdownContainer}>
        <div style={styles.chartLikeHorizontalSpacer} />
        <div style={styles.chartLikeHorizontalSpacer}>
          <TimescaleDropdown
            value={timescale}
            onChange={this.changeTimescale}
          />
          </div>
      </div>
    );
  }

  render() {
    const { resources, width } = this.props;
    const hasData = !!this.getDataForRouteKey(ChartConstants.ORDERS_ANALYTICS_API_ROUTE);
    if (!hasData || resources.locations === undefined) return <LoadingBar />;

    return Functions.isDeviceView('md', width) ? this.renderMobileHomePage() : this.renderHomePage();
  }
}

const TimescaleDropdown = ({ value, onChange }) => (
  <Select
    value={value}
    style={dropdownStyles.homePageTimescaleDropdownOuterContainer}
    input={<InputBase startAdornment={<TimescaleDropdownIcon />} />}
    onChange={onChange}
    IconComponent={ExpandMore}
  >
    {
      ChartConstants.ANALYTICS_TIMESCALE_OPTIONS.map(timescale => (
        <MenuItem key={timescale.key} value={timescale.key}>
          {timescale.name}
        </MenuItem>
      ))
    }
  </Select>
);

const TimescaleDropdownIcon = props => (
  <InputAdornment position="start">
    <DateRange />
  </InputAdornment>
);

const DownloadsChart = ({ renderChart, title, isMobile }) => (
  <Paper style={isMobile ? styles.mobilePaper : styles.paper}>
    <div style={styles.chartElement}>
      <div style={styles.downloadsTitleAndLegendWrapper}>
        <div style={styles.downloadsTitle}>{title}</div>
        <DownloadsLegend />
      </div>
      {renderChart}
    </div>
  </Paper>
);


const DownloadsLegend = props => (
  <div style={styles.downloadsLegendOuterDiv}>
    <DownloadsLegendItems />
  </div>
);

const DownloadsLegendItems = props => (
  ChartConstants.STATIC_LEGENDS.downloads.map((item, ind) => {
    const { title, color } = item;
    return (
      <div style={styles.downloadsLegendItem} key={ind}>
        <LegendColorBox color={color} />
        <LegendText text={title} />
      </div>
    );
  })
);

const OneQuadrantLineChart = ({ renderChart, title, isMobile }) => (
  <Paper style={isMobile ? styles.mobilePaper : styles.paper}>
    <div style={styles.chartElement}>
      <div style={styles.title}>{title}</div>
      {renderChart}
    </div>
  </Paper>
);

const HalfQuadrantPieChart = ({ renderChart, title }) => (
  <div style={styles.halfQuadrantChartElement}>
    <div style={styles.centeredTitle}>{title}</div>
    {renderChart}
  </div>
);

Home.propTypes = {
  loading: PropTypes.bool.isRequired,
  resources: PropTypes.objectOf(PropTypes.any).isRequired,
  actions: PropTypes.objectOf(PropTypes.any).isRequired,
  apiToken: PropTypes.string.isRequired,
  filter: PropTypes.objectOf(PropTypes.any).isRequired,
};

const mapStateToProps = state => ({
  loadingOrdersAnalytics: Selectors.isLoadingOrdersAnalytics(state),
  loadingDownloadsAnalytics: Selectors.isLoadingDownloadsAnalytics(state),
});

const pipeline = flowRight([withRouter, connect(mapStateToProps), withWidth()]);
const pipelineForTests = flowRight([withRouter, connect(mapStateToProps)]);
export default pipelineForTests(Home);
export const WithWidthHome = pipeline(Home);
